import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { DateValue } from '@react-types/calendar';
import {
  Button,
  DatePicker,
  Popover,
  PopoverTrigger,
  WeightField,
  type Weight,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue } from 'jotai';

import { $checkLoadPhotoUploadsValidity } from '../../_state/loadDetail.state';
import { tradeRoutePaths } from '../../routes';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';
import { handleActionableError } from '~/modules/trade/utils/errorHandling';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export interface Props {
  onMarkAsShipped: (
    shippedDate: CalendarDate | undefined,
    lightWeight: Weight | null,
    heavyWeight: Weight | null,
  ) => void;
  load?: Pick<
    LoadRecord,
    | 'status'
    | 'id'
    | 'salesOrderId'
    | 'bookingId'
    | 'loadNumber'
    | 'lineItems'
    | 'facilityId'
    | 'accountId'
    | 'shippedDate'
    | 'reviewed'
    | 'files'
  >;
  isLoading?: boolean;
  shippedDate?: CalendarDate | undefined;
  popoverPlacement?: 'top' | 'bottom';
}

export const LoadStatusTransitionMarkAsShippedButton: React.FC<Props> = ({
  onMarkAsShipped,
  load,
  isLoading,
  shippedDate: initialShippedDate,
  // popoverPlacement = 'bottom',
}) => {
  const t = useTranslations();
  const toast = useToast();
  const [, navigate] = useModuleLocation();
  const [open, setOpen] = React.useState(false);
  const [shippedDate, setShippedDate] = React.useState<DateValue | null>(null);
  const [lightWeight, setLightWeight] = React.useState<Weight | null>(null);
  const [heavyWeight, setHeavyWeight] = React.useState<Weight | null>(null);

  React.useEffect(() => {
    if (initialShippedDate) {
      setShippedDate(initialShippedDate);
    }
  }, [initialShippedDate]);

  const checkLoadPhotoUploadsValidity = useAtomValue(
    $checkLoadPhotoUploadsValidity,
  );

  const handleButtonPress = React.useCallback(async () => {
    if (load) {
      const canShip = load.bookingId
        ? await checkLoadPhotoUploadsValidity(load.id, load.bookingId)
        : await Promise.resolve(true);

      if (!canShip) {
        // Check if we're already on the load detail page
        navigate(tradeRoutePaths.loadDetail, {
          params: { id: load.id },
          query: { tab: 'photos' },
          keepSearchParams: false,
        });

        return; // Don't show popover if validation fails
      }
    }
    setOpen((v) => !v);
  }, [checkLoadPhotoUploadsValidity, load, navigate]);

  const missingShippingDateError =
    t.loadStatusTransition.markAsShippedPopover.missingShipDate;

  const handleMarkAsShippedClick = React.useCallback(async () => {
    try {
      if (shippedDate) {
        await onMarkAsShipped(
          shippedDate as unknown as CalendarDate,
          lightWeight,
          heavyWeight,
        );
        setOpen(false);
        return;
      }

      throw new Error(missingShippingDateError);
    } catch (error) {
      handleActionableError(error, toast, navigate);
    }
  }, [
    shippedDate,
    missingShippingDateError,
    onMarkAsShipped,
    lightWeight,
    heavyWeight,
    toast,
    navigate,
  ]);

  const handleDiscard = () => {
    setOpen(false);
    setShippedDate(null);
    setLightWeight(null);
    setHeavyWeight(null);
  };

  return (
    <PopoverTrigger>
      <Button variant="solid" isLoading={isLoading} onPress={handleButtonPress}>
        {t.loadStatusTransition.markAsShippedPopover.markAsShipped}
      </Button>
      <Popover isOpen={open} onOpenChange={setOpen}>
        <div className="w-auto rounded-md bg-white p-4 shadow-lg">
          <div className="flex flex-col gap-4">
            <DatePicker
              value={shippedDate}
              onChange={setShippedDate}
              className="w-[255px]"
              label={t.loadStatusTransition.markAsShippedPopover.shippedDate}
              isRequired={true}
              isDisabled={false}
            />
            <WeightField
              weightValue={lightWeight}
              onWeightValueChange={setLightWeight}
              defaultUnit="Pounds"
              unitLocked
              className="w-[255px]"
              label={t.loadStatusTransition.markAsShippedPopover.lightWeight}
              isRequired={true}
              isDisabled={false}
            />
            <WeightField
              weightValue={heavyWeight}
              onWeightValueChange={setHeavyWeight}
              defaultUnit="Pounds"
              unitLocked
              className="w-[255px]"
              label={t.loadStatusTransition.markAsShippedPopover.heavyWeight}
              isRequired={true}
              isDisabled={false}
            />
          </div>
          <div className="col-span-2 mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              type="reset"
              className="w-full"
              onPress={handleDiscard}
            >
              {t.loadStatusTransition.markAsShippedPopover.discard}
            </Button>
            <Button
              variant="solid"
              onPress={handleMarkAsShippedClick}
              isLoading={isLoading}
            >
              {t.loadStatusTransition.markAsShippedPopover.markAsShipped}
            </Button>
          </div>
        </div>
      </Popover>
    </PopoverTrigger>
  );
};
